import { useEffect, useState } from "react";
import { DyteParticipantsAudio } from "@dytesdk/react-ui-kit";
import { DyteProvider, useDyteClient } from "@dytesdk/react-web-core";
import { DyteParticipant } from "@dytesdk/web-core";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import { getUserAuthTokenForMeeting } from "../../data/appointments";
import CallHeader from "./components/CallHeader";
import ParticipantsPanel from "./components/ParticipantsPanel";
import RemoteVideoTile from "./components/RemoteVideoTile";
import UserVideoTile from "./components/UserVideoTile";
import VideoControls from "./components/VideoControls";
import VideoLoading from "./components/VideoLoading";

export function VideoCall() {
  const navigate = useNavigate();
  const location = useLocation();
  const mentorName = location.state?.mentorName;
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("sessionId") || "";

  if (!sessionId) {
    throw new Error("Session ID is required");
  }

  const [meeting, initMeeting] = useDyteClient();

  const [isMicOn, setIsMicOn] = useState(false);
  const [isVideoOn, setIsVideoOn] = useState(false);
  const [showParticipants, setShowParticipants] = useState(false);
  const [remoteParticipant, setRemoteParticipant] =
    useState<DyteParticipant | null>(null);

  useEffect(() => {
    async function initalizeMeeting() {
      const authToken = await getUserAuthTokenForMeeting(sessionId, "mentee");

      if (!authToken) {
        alert(
          "An authToken wasn't passed, please pass an authToken in the URL query to join a meeting."
        );
        return;
      }

      const meeting = await initMeeting({
        authToken,
        defaults: {
          audio: false,
          video: false,
        },
      });

      await meeting!.joinRoom();
    }

    if (!meeting) {
      initalizeMeeting();
    }
  }, [meeting]);

  useEffect(() => {
    if (!meeting) return;
    const activeParticipants = meeting.participants.active.toArray();
    const initialRemoteParticipant = activeParticipants[0];
    if (initialRemoteParticipant) {
      setRemoteParticipant(initialRemoteParticipant);
    }

    const handleParticipantJoined = (participant: DyteParticipant) => {
      setRemoteParticipant(participant);
    };

    const handleParticipantLeft = () => {
      setRemoteParticipant(null);
    };

    meeting.participants.joined.on(
      "participantJoined",
      handleParticipantJoined
    );
    meeting.participants.active.on("participantLeft", handleParticipantLeft);

    return () => {
      meeting.participants.active.removeListener(
        "participantJoined",
        handleParticipantJoined
      );
      meeting.participants.active.removeListener(
        "participantLeft",
        handleParticipantLeft
      );

      meeting.leaveRoom();
    };
  }, [meeting]);

  const handleLeaveCall = () => {
    // meeting.leave();
    navigate(`/mentee/appointments`);
  };

  return (
    <DyteProvider value={meeting} fallback={<VideoLoading />}>
      <div className="relative flex min-h-screen flex-col overflow-hidden bg-gray-900">
        {/* Call Header */}
        <CallHeader
          showParticipants={showParticipants}
          setShowParticipants={setShowParticipants}
        />
        {/* Main Video Area */}
        <div className="relative flex-1">
          {/* Mentor's Video (Full Screen) */}
          <RemoteVideoTile remoteParticipant={remoteParticipant} />
          {/* <div className="absolute inset-0 bg-gray-800">
            <div className="relative flex size-full items-center justify-center overflow-hidden">
              <img
                src="/60695538-3ffa-4211-9645-68b0244a31fe.png"
                alt="Jay Curran"
                className="size-full object-cover"
              />
            </div>
          </div> */}

          {/* User's Video (Thumbnail) */}
          <UserVideoTile isVideoOn={isVideoOn} isMicOn={isMicOn} />

          {/* Participants Panel */}
          <ParticipantsPanel
            show={showParticipants}
            isMicOn={isMicOn}
            isVideoOn={isVideoOn}
            onClose={() => setShowParticipants(false)}
          />
        </div>
        {/* Video Controls */}
        <VideoControls
          isMicOn={isMicOn}
          setIsMicOn={setIsMicOn}
          isVideoOn={isVideoOn}
          setIsVideoOn={setIsVideoOn}
          showParticipants={showParticipants}
          setShowParticipants={setShowParticipants}
          handleLeaveCall={handleLeaveCall}
        />
        {/* Session Info */}
        <div className="absolute left-4 top-20 max-w-xs rounded-lg bg-black/50 p-3 text-white backdrop-blur-sm">
          <div className="mb-1 text-sm font-medium">
            Sales Mentoring Session
          </div>
          <div className="text-xs text-white/70">
            50-minute session with {mentorName}
          </div>
        </div>
      </div>
      <DyteParticipantsAudio meeting={meeting} />
    </DyteProvider>
  );
}
